import { IFilTerValue, IGetCreators, IGetFaectValue } from "@shared/lib/types";
import { SocialMediaPlatform } from "@shared/lib/types/influencer";

import { ToastService } from "@shared/lib/services";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { DiscoveryService } from "../services/discovery.service";

interface SelectedFieldState {
  data: IGetCreators | undefined;
  facets: IGetFaectValue;
  selectedFilters: { [key: string]: IFilTerValue };
  backendFIlters: { [key: string]: IFilTerValue };

  platform: string;
  username: string;
  isLoadingData: boolean;
}

interface SelectedFieldActions {
  fetchData: (
    limit: number,
    pageNo: number,
    bucketId?: string
  ) => Promise<void>;

  fetchFacets: () => Promise<void>;
  setPlatform: (platform: string) => void;
  setUsername: (username: string) => void;
  resetFilters: () => void;
  saveFilters: () => void;

  updateFilters: (key: string, filterValue: IFilTerValue | undefined) => void;
  setFiltersFromUrl: (filters: { [key: string]: IFilTerValue }) => void;
  reset: () => void;
  fullReset: () => void;
}

const getInitialState = (): SelectedFieldState => ({
  data: undefined,
  facets: {},
  platform: SocialMediaPlatform.Instagram,
  username: "",
  selectedFilters: {},
  backendFIlters: {},
  isLoadingData: false,
});

export const useGetInfluencerStore = create<
  SelectedFieldState & SelectedFieldActions
>()(
  devtools((set, get) => ({
    ...getInitialState(),

    fetchData: (() => {
      let activeRequestId = 0;

      return async (
        limit: number,
        pageNo: number,
        bucketId?: string
      ): Promise<void> => {
        const { platform, username, backendFIlters } = get();

        set({ isLoadingData: true });

        // Generate a new request ID
        const requestId = ++activeRequestId;

        try {
          const response = await DiscoveryService.getCreators(
            platform,
            username,
            limit,
            pageNo,
            backendFIlters,
            bucketId
          );
          const data = await response.data;

          // Only update state if this is the latest request
          if (requestId === activeRequestId) {
            set({ data, selectedFilters: backendFIlters });
          }
        } catch (error) {
          if (requestId === activeRequestId) {
            console.error("Error fetching influencers:", error);
            ToastService.error("Error fetching influencers");
          }
        } finally {
          if (requestId === activeRequestId) {
            set({ isLoadingData: false });
          }
        }
      };
    })(),

    fetchFacets: async () => {
      try {
        // Fetch facets (Replace with actual API call)
        const response = await DiscoveryService.getFacets();
        const facets = response.data;
        set({ facets });
      } catch (error) {
        console.error("Error fetching facets:", error);
      }
    },
    setPlatform: (platform) => set({ platform }),

    setUsername: (username) => set({ username }),

    resetFilters: () => {
      const { backendFIlters } = get();
      set({ selectedFilters: { ...backendFIlters } });
    },
    saveFilters: () => {
      const { selectedFilters } = get();
      set({ backendFIlters: { ...selectedFilters } });
    },

    updateFilters: (key, filterValue) => {
      const { selectedFilters } = get();
      const updatedFilters = { ...selectedFilters };
      if (filterValue === undefined) {
        delete updatedFilters[key];
        set((state) => ({
          selectedFilters: updatedFilters,
        }));
      } else {
        set((state) => ({
          selectedFilters: { ...updatedFilters, [key]: filterValue },
        }));
      }
    },

    reset: () =>
      set((state) => ({
        ...getInitialState(),
        platform: state.platform,
        username: state.username,
      })),
    fullReset: () =>
      set((state) => ({
        ...getInitialState(),
      })),

    setFiltersFromUrl: (filters: { [key: string]: IFilTerValue }) => {
      set({ backendFIlters: { ...filters }, selectedFilters: { ...filters } });
    },
  }))
);
